import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { FiCheck, FiAlertCircle, FiEye, FiEyeOff, FiLock, FiUser } from 'react-icons/fi';
import * as invitationsApi from '../../api/invitations';
import type { InvitationDetails } from '../../api/invitations';
import logo from '../Assets/logogeniac.png';

const ROLE_LABELS: Record<string, string> = {
  admin:  'Admin',
  member: 'Membre',
  viewer: 'Lecteur',
};

type PageState = 'loading' | 'invalid' | 'form' | 'success';

const AcceptInvitationPage: React.FC = () => {
  const [searchParams]  = useSearchParams();
  const navigate        = useNavigate();
  const token           = searchParams.get('token') || '';

  const [pageState, setPageState]     = useState<PageState>('loading');
  const [invitation, setInvitation]   = useState<InvitationDetails | null>(null);
  const [fullName, setFullName]       = useState('');
  const [password, setPassword]       = useState('');
  const [confirm, setConfirm]         = useState('');
  const [showPwd, setShowPwd]         = useState(false);
  const [submitting, setSubmitting]   = useState(false);
  const [formError, setFormError]     = useState<string | null>(null);

  useEffect(() => {
    if (!token) { setPageState('invalid'); return; }

    invitationsApi.getInvitationByToken(token)
      .then(inv => {
        setInvitation(inv);
        setPageState('form');
      })
      .catch(() => setPageState('invalid'));
  }, [token]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);

    if (password.length < 8) {
      setFormError('Le mot de passe doit contenir au moins 8 caractères.');
      return;
    }
    if (password !== confirm) {
      setFormError('Les mots de passe ne correspondent pas.');
      return;
    }

    setSubmitting(true);
    try {
      await invitationsApi.acceptInvitation(token, { full_name: fullName, password });
      setPageState('success');
    } catch (err: any) {
      const msg = err?.response?.data?.message || err?.response?.data?.error || 'Une erreur est survenue.';
      setFormError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center px-4 py-12">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="flex justify-center mb-8">
          <img src={logo} alt="GenIaC" className="h-10 object-contain" />
        </div>

        {/* Loading */}
        {pageState === 'loading' && (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-10 text-center">
            <div className="w-10 h-10 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
            <p className="text-gray-500">Vérification de l'invitation…</p>
          </div>
        )}

        {/* Invalid / expired */}
        {pageState === 'invalid' && (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-10 text-center">
            <div className="w-14 h-14 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <FiAlertCircle size={28} className="text-red-500" />
            </div>
            <h1 className="text-xl font-bold text-gray-900 mb-2">Invitation invalide</h1>
            <p className="text-gray-500 text-sm mb-6">
              Ce lien d'invitation est invalide ou a expiré. Demandez à votre administrateur de vous renvoyer une invitation.
            </p>
            <Link to="/login" className="text-blue-600 text-sm font-medium hover:underline">
              Retour à la connexion
            </Link>
          </div>
        )}

        {/* Form */}
        {pageState === 'form' && invitation && (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
            {/* Header */}
            <div className="bg-blue-600 px-8 py-6 text-white">
              <p className="text-blue-200 text-sm mb-1">Invitation à rejoindre</p>
              <h1 className="text-2xl font-bold">{invitation.organization_name}</h1>
              <div className="flex items-center gap-2 mt-2 text-sm text-blue-100">
                <span>{invitation.email}</span>
                <span>·</span>
                <span className="bg-blue-500 px-2 py-0.5 rounded-full text-xs font-medium">
                  {ROLE_LABELS[invitation.role] || invitation.role}
                </span>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="px-8 py-6 space-y-5">
              <p className="text-sm text-gray-500">Créez votre compte pour accéder à la plateforme.</p>

              {formError && (
                <div className="flex items-start gap-2 bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-red-700 text-sm">
                  <FiAlertCircle size={16} className="shrink-0 mt-0.5" />
                  <span>{formError}</span>
                </div>
              )}

              {/* Full name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nom complet</label>
                <div className="relative">
                  <FiUser className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
                  <input
                    type="text"
                    value={fullName}
                    onChange={e => setFullName(e.target.value)}
                    placeholder="Prénom Nom"
                    required
                    className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                  />
                </div>
              </div>

              {/* Email (readonly) */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                <input
                  type="email"
                  value={invitation.email}
                  readOnly
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-lg bg-gray-50 text-gray-500 text-sm cursor-not-allowed"
                />
              </div>

              {/* Password */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Mot de passe</label>
                <div className="relative">
                  <FiLock className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
                  <input
                    type={showPwd ? 'text' : 'password'}
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    placeholder="Minimum 8 caractères"
                    required
                    minLength={8}
                    className="w-full pl-10 pr-10 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPwd(v => !v)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showPwd ? <FiEyeOff size={16} /> : <FiEye size={16} />}
                  </button>
                </div>
              </div>

              {/* Confirm password */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Confirmer le mot de passe</label>
                <div className="relative">
                  <FiLock className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
                  <input
                    type={showPwd ? 'text' : 'password'}
                    value={confirm}
                    onChange={e => setConfirm(e.target.value)}
                    placeholder="Répétez le mot de passe"
                    required
                    className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={submitting || !fullName || !password || !confirm}
                className="w-full py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center gap-2 transition-colors"
              >
                {submitting
                  ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  : null}
                Créer mon compte et rejoindre
              </button>

              <p className="text-xs text-center text-gray-400">
                En rejoignant, vous acceptez les{' '}
                <a href="/terms" className="underline">conditions d'utilisation</a> de GenIaC.
              </p>
            </form>
          </div>
        )}

        {/* Success */}
        {pageState === 'success' && invitation && (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-10 text-center">
            <div className="w-14 h-14 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <FiCheck size={28} className="text-green-500" />
            </div>
            <h1 className="text-xl font-bold text-gray-900 mb-2">Compte créé !</h1>
            <p className="text-gray-500 text-sm mb-6">
              Bienvenue dans <strong>{invitation.organization_name}</strong>. Connectez-vous pour commencer à utiliser GenIaC.
            </p>
            <button
              onClick={() => navigate('/login', { state: { email: invitation.email } })}
              className="w-full py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors"
            >
              Se connecter
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default AcceptInvitationPage;
